// src/Tableros/ScoreboardLynx/TableroVentana.js
import React, { useState, useEffect } from 'react';
import SpeedResultTable from '../Tablas/SpeedResultTable';
import socketSyncService from '../../../services/SocketSyncService';
import { useSearchParams } from 'react-router-dom';

const TableroVentana = () => {
  // Obtener el ID de la configuración desde la URL
  const [searchParams] = useSearchParams();
  const configId = searchParams.get('id');

  // Estados para configuración y datos en tiempo real
  const [config, setConfig] = useState(null);
  const [results, setResults] = useState([]);
  const [eventDetails, setEventDetails] = useState({
    eventName: 'Cargando...',
    event: '',
    round: '',
    heat: '',
    wind: '',
    participants: 0,
  });
  const [clock, setClock] = useState('0.0');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Función para eliminar los decimales del tiempo
  const formatTime = (timeString) => {
    if (!timeString) return '0';
    return timeString.split('.')[0];
  };

  // Cargar configuración desde localStorage
  useEffect(() => {
    try {
      const savedConfig = localStorage.getItem(`config-${configId}`);
      if (savedConfig) {
        console.log('Configuración cargada:', JSON.parse(savedConfig));
        setConfig(JSON.parse(savedConfig));
      } else {
        setError('No se encontró la configuración del tablero.');
        console.error('No se encontró configuración para el ID:', configId);
      }
    } catch (error) {
      setError('Error al cargar la configuración.');
      console.error('Error al cargar la configuración:', error);
    }
    
    setIsLoading(false);
  }, [configId]);

  // Configurar la sincronización con socketSyncService
  useEffect(() => {
    // Manejar mensajes recibidos
    const handleMessage = (message) => {
      try {
        const obj = JSON.parse(message);
        if (obj.length < 2) return;
        
        // Procesar los detalles del evento
        if (obj[1] && obj[1].length >= 6) {
          const newEventDetails = {
            eventName: obj[1][1] || 'No hay eventos seleccionados',
            wind: obj[1][2] || 'NM',
            event: obj[1][3] || '0',
            round: obj[1][4] || '0',
            heat: obj[1][5] || '0',
            participants: obj[1][8] || '0',
          };
          setEventDetails(newEventDetails);
          
          if (newEventDetails.eventName === 'No hay eventos seleccionados') {
            // Resetear datos
            setResults([]);
            setClock('0.0');
          }
        }
        
        // Procesar el reloj
        if (obj[0]) {
          const formattedClock = formatTime(obj[0][0]);
          setClock(formattedClock);
        }
        
        // Procesar los resultados
        if (obj.length > 2) {
          const newResults = [];
          for (let i = 2; i < obj.length; i++) {
            newResults.push(obj[i]);
          }
          setResults(newResults);
        }
      } catch (error) {
        console.error('Error procesando mensaje:', error);
      }
    };
    
    // Manejar evento clearBoard
    const handleClearBoard = () => {
      setResults([]);
      setClock('0.0');
    };
    
    // Registrar manejadores de eventos
    const unsubscribeMessage = socketSyncService.on('message', handleMessage);
    const unsubscribeClear = socketSyncService.on('clearBoard', handleClearBoard);
    
    // Limpiar al desmontar
    return () => {
      unsubscribeMessage();
      unsubscribeClear();
    };
  }, []);

  // Manejar mensajes de actualización de la ventana principal
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'updateConfig') {
        console.log('Recibida solicitud de actualización');
        try {
          const savedConfig = localStorage.getItem(`config-${configId}`);
          if (savedConfig) {
            console.log('Configuración actualizada:', JSON.parse(savedConfig));
            setConfig(JSON.parse(savedConfig));
          }
        } catch (error) {
          console.error('Error al actualizar la configuración:', error);
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [configId]);

  // Optimización para reducir retrasos en el cronómetro
  useEffect(() => {
    // Esta función se ejecutará cada frame para hacer que la actualización del reloj sea más suave
    let animationFrameId;
    let lastTimestamp = 0;
    
    const updateClock = (timestamp) => {
      // Solo actualizar si han pasado al menos 16ms (aprox. 60fps)
      if (timestamp - lastTimestamp >= 16) {
        lastTimestamp = timestamp;
        // No hacemos nada más aquí, solo garantizamos que el componente se renderice suavemente
      }
      
      animationFrameId = requestAnimationFrame(updateClock);
    };
    
    animationFrameId = requestAnimationFrame(updateClock);
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [clock]); // Solo re-iniciar cuando clock cambia

  // Mostrar indicador de carga o error
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh', 
        backgroundColor: '#1a1a1a', 
        color: '#ffffff',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Cargando tablero...</h2>
          <p>Conectando con el servidor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh', 
        backgroundColor: '#1a1a1a', 
        color: '#ffffff',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Error al cargar el tablero</h2>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              background: '#3f51b5',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '20px'
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh', 
        backgroundColor: '#1a1a1a', 
        color: '#ffffff',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2>No se encontró la configuración</h2>
          <p>La configuración del tablero no está disponible o ha expirado.</p>
        </div>
      </div>
    );
  }

  // Extraer la configuración
  const {
    showLane = false,
    showNumber = false,
    showLastLapTime = false,
    showLapsToGo = false,
    showLapsColumns = false,
    showReactionTime = false,
    showTimeDifference = false,
    displayRows = 8,
    tableWidth = 800,
    tableHeight = 600,
    tableBackgroundColor = '#1a1a1a',
    globalFontColor = '#ffffff',
    timerColor = '#00ff00',
    headerFontSize = 24,
    nameFontSize = 18,
    positionFontSize = 20,
    timeFontSize = 18,
    stopwatchSize = 50,
    headerFontColor = '#ffffff',
  } = config.settings;

  return (
    <SpeedResultTable
      results={results}
      eventName={eventDetails}
      clock={clock}
      showLane={showLane}
      showNumber={showNumber}
      showLastLapTime={showLastLapTime}
      showLapsToGo={showLapsToGo}
      showLapsColumns={showLapsColumns}
      displayRows={displayRows}
      showReactionTime={showReactionTime}
      showTimeDifference={showTimeDifference}
      tableWidth={tableWidth}
      tableHeight={tableHeight}
      tableBackgroundColor={tableBackgroundColor}
      globalFontColor={globalFontColor}
      timerColor={timerColor}
      headerFontSize={headerFontSize}
      nameFontSize={nameFontSize}
      positionFontSize={positionFontSize}
      timeFontSize={timeFontSize}
      stopwatchSize={stopwatchSize}
      headerFontColor={headerFontColor}
    />
  );
};

export default TableroVentana;